/**
 * ARGOS Field PWA — canonical design tokens.
 *
 * Mobile-first port of the data-vault web app's visual language. Pulled
 * down a notch: bigger touch targets, less hover-reliance, more :active
 * feedback. Import these strings instead of re-inlining utility classes
 * across pages so visual changes propagate from one place.
 */

/** Card surfaces — primary panels on every page. */
export const CARD_CLS =
  'rounded-xl bg-mono-900/70 border border-mono-800 shadow-soft backdrop-blur-sm';

/** Subtle card — for grouped content inside a primary card. */
export const SUBCARD_CLS =
  'rounded-lg bg-mono-900/40 border border-mono-800/70';

/** Input field — text/number/date. */
export const FIELD_CLS =
  'min-h-touch w-full rounded-md border border-mono-700 bg-mono-900 text-mono-100 ' +
  'px-3 py-2 text-base placeholder:text-mono-500 ' +
  'focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 ' +
  'transition-colors';

/** Smaller text input — for inline single-line values. */
export const INPUT_INLINE_CLS =
  'min-h-touch w-full rounded-md border border-mono-700 bg-mono-900 text-mono-100 ' +
  'px-2.5 py-1.5 text-sm placeholder:text-mono-500 ' +
  'focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30';

/** Pill / chip — non-interactive label. */
export const PILL_CLS =
  'inline-flex items-center gap-1.5 text-xs text-mono-300 ' +
  'bg-mono-800/60 border border-mono-700/60 rounded-full px-2.5 py-1';

/** Pill — interactive (tap to toggle). */
export const PILL_BTN_CLS =
  PILL_CLS + ' min-h-touch active:bg-mono-700/60 transition-colors';

/** Primary call-to-action — full-width on mobile. */
export const BTN_PRIMARY_CLS =
  'min-h-touch w-full rounded-md bg-cyan-500 hover:bg-cyan-400 active:bg-cyan-600 ' +
  'text-mono-950 font-semibold px-4 py-2 disabled:opacity-50 ' +
  'transition-colors shadow-soft';

/** Secondary action — neutral border + text. */
export const BTN_SECONDARY_CLS =
  'min-h-touch w-full rounded-md border border-mono-700 hover:border-mono-500 ' +
  'active:bg-mono-800 text-mono-100 px-4 py-2 disabled:opacity-50 transition-colors';

/** Danger action — destructive. */
export const BTN_DANGER_CLS =
  'min-h-touch w-full rounded-md border border-red-700/60 hover:border-red-500/80 ' +
  'active:bg-red-950/40 text-red-300 px-4 py-2 disabled:opacity-50 transition-colors';

/** Icon button — square, 44×44 touch target. */
export const ICON_BTN_CLS =
  'min-h-touch min-w-touch flex items-center justify-center rounded-md ' +
  'border border-mono-700/60 hover:border-cyan-500/60 active:bg-mono-800 ' +
  'text-mono-300 hover:text-cyan-300 transition-colors';

/** Status tones — keyed by per-record sync / wizard state. */
export const STATUS_TONES = {
  planned: {
    bg: 'bg-mono-700/40',
    text: 'text-mono-300',
    ring: 'ring-1 ring-mono-600/40',
    bar: 'bg-mono-500',
  },
  in_progress: {
    bg: 'bg-amber-500/15',
    text: 'text-amber-300',
    ring: 'ring-1 ring-amber-500/30',
    bar: 'bg-amber-500',
  },
  complete: {
    bg: 'bg-emerald-500/15',
    text: 'text-emerald-300',
    ring: 'ring-1 ring-emerald-500/30',
    bar: 'bg-emerald-500',
  },
  flagged: {
    bg: 'bg-red-500/15',
    text: 'text-red-300',
    ring: 'ring-1 ring-red-500/30',
    bar: 'bg-red-500',
  },
  syncing: {
    bg: 'bg-cyan-500/15',
    text: 'text-cyan-300',
    ring: 'ring-1 ring-cyan-500/30',
    bar: 'bg-cyan-500',
  },
} as const;

export type StatusTone = keyof typeof STATUS_TONES;

/** GPS accuracy buckets — drive GpsBadge color. */
export const GPS_TONES = {
  good:   { color: 'text-emerald-400', ring: 'ring-emerald-500/40', label: 'Good' },
  ok:     { color: 'text-amber-400',   ring: 'ring-amber-500/40',   label: 'OK' },
  poor:   { color: 'text-red-400',     ring: 'ring-red-500/40',     label: 'Poor' },
  unknown:{ color: 'text-mono-400',    ring: 'ring-mono-600/40',    label: 'Acquiring' },
} as const;

/** Sticky bottom action bar — used by wizard + ad-hoc submit. */
export const STICKY_BOTTOM_CLS =
  'sticky bottom-0 -mx-4 px-4 pt-3 pb-safe-4 bg-mono-950/95 backdrop-blur ' +
  'border-t border-mono-800';

/** Sticky top stepper bar. */
export const STICKY_TOP_CLS =
  'sticky top-0 -mx-4 px-4 py-2.5 bg-mono-950/95 backdrop-blur ' +
  'border-b border-mono-800 z-10';

/** Section-label classes — small uppercase tracking-widest captions. */
export const LABEL_CLS = 'text-[10px] tracking-widest uppercase text-mono-500';

/** Standard text sizes used across the PWA. */
export const TEXT = {
  pageTitle: 'text-2xl font-bold text-mono-100',
  cardTitle: 'text-base font-semibold text-mono-100',
  caption: 'text-xs text-mono-400',
} as const;
